import React from 'react'
import { FormattedMessage, MessageDescriptor } from 'react-intl'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  paragraphHeaderCss,
  paragraphTextCss
} from '../../legacy-design-system/text'
import ReactDOM from 'react-dom'

export interface ItemProps {
  readonly title: MessageDescriptor
  readonly description: Record<string, any>
  readonly imagePath: string
  readonly imageCss?: string
  readonly circleIndicators?: CircleIndicatorProps[]
}

interface CircleIndicatorProps {
  readonly selector: string
  readonly radius: number
  readonly yOffset: number
  readonly xOffset: number
  readonly zIndex?: number
}

interface State {
  readonly isOver: boolean
}

const itemPaddingTop = 14
const firstItemPaddingTop = itemPaddingTop + 15

const mainContainerCss = css`
  display: flex;
  flex-direction: row;
  padding: ${itemPaddingTop}px 45px 18px 22px;
  background-color: ${theme.color.neutral10};
  border-top: 1px solid transparent;
  border-bottom: 1px solid transparent;
  &:hover {
    background-color: ${theme.color.neutral10};
    border-top: 1px solid ${theme.color.neutral30};
    border-bottom: 1px solid ${theme.color.neutral30};
  }
  &:first-child {
    padding-top: ${firstItemPaddingTop}px;
  }
`

const contentContainerCss = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-left: 15px;
`

const titleCss = css`
  padding-bottom: 6px;
`

const descriptionCss = css`
  line-height: 1.25;
  div {
    padding-top: 10px;
    font-size: 12px;
    color: ${theme.color.neutral60};
  }
  a {
    cursor: pointer;
  }
`

class CircleIndicator extends React.Component<CircleIndicatorProps> {
  public render() {
    const { selector, radius, yOffset, xOffset, zIndex } = this.props
    let portal
    let top
    let left

    // eslint-disable-next-line prefer-const
    portal = document.querySelector(selector || '')
    if (portal) {
      const position = getPosition(portal)
      top = position.y + yOffset
      left = position.x + xOffset
    }

    return (
      portal &&
      ReactDOM.createPortal(
        <Ring radius={radius} top={top} left={left} zIndex={zIndex} />,
        portal
      )
    )
  }
}

interface RingProps {
  readonly radius: number
  readonly top: number
  readonly left: number
  readonly zIndex?: number
}

const getCircleCss = (
  top: number,
  left: number,
  diameter: number,
  border: number,
  zIndex: number
) => {
  return css`
    z-index: ${zIndex};
    position: fixed;
    top: ${top}px;
    left: ${left}px;
    width: ${diameter}px;
    height: ${diameter}px;
    background: rgba(252, 252, 252, 0.4);
    border-radius: 100%;
    -webkit-border-radius: 100%;
    -moz-border-radius: 100%;
    border: ${border}px solid ${theme.color.teal50};
    box-shadow: 2px 0px 2px 0px rgba(0, 0, 0, 0.3);
  `
}

class Ring extends React.Component<RingProps> {
  public render() {
    const border = 4
    const { radius, top, left, zIndex = 1001 } = this.props
    const diameter = (radius - border) * 2
    const circleCss = getCircleCss(top, left, diameter, border, zIndex)
    return <div className={circleCss} />
  }
}

function getPosition(el) {
  let xPos = 0
  let yPos = 0

  while (el) {
    if (el.tagName === 'BODY') {
      // deal with browser quirks with body/window/document and page scroll
      const xScroll =
        el.scrollLeft || (document.documentElement as any).scrollLeft
      const yScroll =
        el.scrollTop || (document.documentElement as any).scrollTop

      xPos += el.offsetLeft - xScroll + el.clientLeft
      yPos += el.offsetTop - yScroll + el.clientTop
    } else {
      // for all other non-BODY elements
      xPos += el.offsetLeft - el.scrollLeft + el.clientLeft
      yPos += el.offsetTop - el.scrollTop + el.clientTop
    }

    el = el.offsetParent
  }

  return {
    x: xPos,
    y: yPos
  }
}

const imageContainerCss = css`
  width: 70px;
`

const imageDefaultCss = css`
  display: block;
  width: 100%;
  height: auto;
`

export class Item extends React.Component<ItemProps, State> {
  public state = {
    isOver: false
  }

  public render() {
    const { title, description, imagePath, imageCss, circleIndicators } =
      this.props

    return (
      <React.Fragment>
        <div
          className={mainContainerCss}
          onMouseEnter={() => this.toggleDrawCircle(true)}
          onMouseLeave={() => this.toggleDrawCircle(false)}
        >
          <div className={imageContainerCss}>
            <img src={imagePath} className={cx(imageDefaultCss, imageCss)} />
          </div>
          <div className={contentContainerCss}>
            <div
              className={cx(paragraphHeaderCss, titleCss)}
              children={<FormattedMessage {...title} />}
            />
            <div
              className={cx(paragraphTextCss, descriptionCss)}
              children={<FormattedMessage {...description} />}
            />
          </div>
        </div>
        {circleIndicators &&
          this.state.isOver &&
          circleIndicators.map((circle, index) => (
            <CircleIndicator key={index} {...circle} />
          ))}
      </React.Fragment>
    )
  }

  private toggleDrawCircle = (over: boolean) => {
    this.setState({ isOver: over })
  }
}
